type ConnectionState = 'online' | 'slow' | 'offline' | 'checking';

/**
 * Indicateur de connexion affiché en haut à gauche sur toutes les pages.
 * Combine les événements navigateur online/offline (réaction instantanée)
 * avec un ping périodique d'un asset statique pour confirmer que le serveur
 * est réellement joignable (navigator.onLine ne renseigne que sur l'interface réseau).
 */
export class ConnectionIndicator {

    private element: HTMLElement;
    private dot: HTMLElement;
    private label: HTMLElement;
    private state: ConnectionState = 'checking';

    // Cible du ping : asset statique servi directement (autorisé par la CSP connect-src 'self')
    private readonly pingUrl: string = '/assets/images/favicon.ico';
    private readonly pingIntervalMs: number = 10000;
    private readonly pingTimeoutMs: number = 4000;
    // Au-delà de cette latence, le ping a réussi mais la connexion est jugée mauvaise (orange)
    private readonly slowThresholdMs: number = 1000;

    constructor() {
        this.element = document.getElementById('connection-indicator') ?? this.createElement();
        this.dot = this.element.querySelector('.connection-indicator__dot') as HTMLElement;
        this.label = this.element.querySelector('.connection-indicator__label') as HTMLElement;

        window.addEventListener('online', () => this.checkConnection());
        window.addEventListener('offline', () => this.setState('offline'));

        this.checkConnection();
        setInterval(() => this.checkConnection(), this.pingIntervalMs);
    }

    private createElement(): HTMLElement {
        const el = document.createElement('div');
        el.id = 'connection-indicator';
        el.className = 'connection-indicator position-fixed start-0 ms-2 d-flex align-items-center gap-2 px-2 py-1 rounded-pill shadow-sm';
        el.style.top = '2px';
        el.style.zIndex = '10000';
        el.style.fontSize = '0.8rem';
        el.style.backgroundColor = 'rgba(255, 255, 255, 0.95)';
        el.style.userSelect = 'none';
        el.style.pointerEvents = 'none';
        el.innerHTML = `
            <span class="connection-indicator__dot rounded-circle" style="width:10px;height:10px;display:inline-block;"></span>
            <span class="connection-indicator__label"></span>
        `;
        (document.body ?? document.documentElement).appendChild(el);
        return el;
    }

    private async checkConnection(): Promise<void> {
        if (!navigator.onLine) {
            this.setState('offline');
            return;
        }

        if (this.state !== 'online') {
            this.setState('checking');
        }

        try {
            const controller = new AbortController();
            const timer = setTimeout(() => controller.abort(), this.pingTimeoutMs);
            const start = performance.now();
            await fetch(`${this.pingUrl}?_=${Date.now()}`, {
                method: 'HEAD',
                cache: 'no-store',
                signal: controller.signal
            });
            clearTimeout(timer);
            const latency = performance.now() - start;
            this.setState(latency > this.slowThresholdMs ? 'slow' : 'online');
        } catch {
            this.setState('offline');
        }
    }

    private setState(state: ConnectionState): void {
        if (state === this.state) {
            return;
        }
        this.state = state;

        const config: Record<ConnectionState, { color: string; text: string }> = {
            online:   { color: '#198754', text: 'En ligne' },
            slow:     { color: '#fd7e14', text: 'Connexion lente' },
            offline:  { color: '#dc3545', text: 'Hors ligne' },
            checking: { color: '#6c757d', text: 'Connexion...' }
        };

        const { color, text } = config[state];
        this.dot.style.backgroundColor = color;
        this.label.textContent = text;
    }
}

// Singleton auto-initialisé
const connectionIndicator = new ConnectionIndicator();

export default connectionIndicator;
